import { App } from 'App'
import { isEqual } from 'lodash'
import { createRoot } from 'react-dom/client'

import { getCurrentPage } from '@subaio/components'
import {
	apiCreate,
	delay,
	initErrorClient,
	keycloakCreate,
	logError,
	onStateChange,
	routerCreate,
	setCorrelationIdPrefix,
} from '@subaio/utils'
import { ContainerStatus, getRoutes } from '@subaio/webapp/src/containers'

import { config } from 'clientConfig'

import type { Container } from 'lib/container'
import { getContainers } from 'lib/containers'
import { storeCreate } from 'lib/store'
import { getUserAuth } from 'lib/userAuth'

initErrorClient(
	{
		enabled: true,
		dataSourceName: config.sentry.dataSourceName,
		release: config.version,
		environment: config.environment,
	},
	() => {
		const {
			page: { name, params, query },
			userAuth: auth,
		} = store.getState()

		return {
			user: auth.status === 'authenticated' ? { id: auth.userId, brandId: auth.brandId } : undefined,
			view: { name, params, query },
		}
	},
)

const store = storeCreate()

const keycloak = keycloakCreate(config.keycloak)

// Persist token to local storage
onStateChange(
	store,
	({ userAuth }) => ({ userAuth }),
	({ userAuth }) => {
		try {
			if (userAuth.status === 'authenticated') {
				localStorage.setItem(
					config.authStorage,
					JSON.stringify({
						auth: { token: userAuth.token, userGroupId: userAuth.userGroupId },
					}),
				)
			} else {
				localStorage.removeItem(config.authStorage)
			}
		} catch (error) {
			logError(error)
		}
	},
	(a, b) => a.userAuth === b.userAuth,
)

// Check for expired token periodically
onStateChange(
	store,
	({ userAuth: auth }) => auth,
	(auth) => {
		if (auth.status !== 'authenticated') return

		const interval = setInterval(() => {
			if (auth.expiration < Date.now()) {
				store.set({ userAuth: { status: 'expired' } })
			}
		}, 60 * 1000)

		return () => {
			clearInterval(interval)
		}
	},
)

// Set correlation-id prefix when relevant
onStateChange(
	store,
	({ userAuth: auth }) => auth,
	(auth) => {
		if (auth.status === 'authenticated') {
			setCorrelationIdPrefix(auth.userGroupId)
		} else {
			setCorrelationIdPrefix(undefined)
		}
	},
)

try {
	const { auth } = JSON.parse(localStorage.getItem(config.authStorage) ?? '')
	store.set({ userAuth: getUserAuth(auth) })
} catch {
	// Suppress error
}

if (config.isDev) {
	;(window as any).store = store
}

const statuspageApi = apiCreate({
	baseUrl: config.api.statuspage,
	shouldRetry: async (error, attempts) => {
		// If the request failed because of a network problem, retry (max 3 times)
		if (error.request.statusCode === 0) {
			// Wait one second longer after each attempt
			await delay(attempts)
			return true
		}

		return false
	},
})

const api = apiCreate({
	baseUrl: '/api',
	logRequestData: config.isDev,
})

const root = createRoot(document.getElementById('root')!)

const render = () => {
	const containers = getContainers<Container>()
	const router = routerCreate(getRoutes(containers))
	const currentPage = getCurrentPage(router)
	const { page, status } = store.getState()
	if (currentPage && (!isEqual(page, currentPage) || status === ContainerStatus.Errored)) {
		store.set({
			page: currentPage,
			status: ContainerStatus.Initializing,
			data: {},
		})
	}
	// Render app
	root.render(
		<App
			api={api}
			containers={containers}
			store={store}
			setToken={(options) => store.set({ userAuth: getUserAuth(options) })}
			statuspageApi={statuspageApi}
			router={router}
			keycloak={keycloak}
			environment={config.environment}
		/>,
	)
}

render()

// Hot Module Replacement API
if (module.hot) {
	module.hot.accept(['App', '@subaio/webapp/src/containers/containers'], () => {
		render()
	})
}
